import React, {
  useState,
  useCallback,
} from 'react';
import { navigate } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import Alert from 'gatsby-theme-core/src/components/alert';
import useCreateSubmission from 'gatsby-theme-forms/src/hooks/use-create-submission';
import styled from 'styled-components';
import FormField from './form-field';
import defaultFields from './fields';
import Success from './success';
import Button from './button';
import { useVehicle } from '../vehicle-context';

const returnAttributes = `
  vehicle {
    data {
      id
      attributes {
        ${process.env.GATSBY_STRAPI_VERSION === '5' ? 'documentId' : ''}
        make
        model
        # for note
        name
        lowestPrice
        # for ascFormSubmissionSales
        vin
        vehicleId
        lowestPrice
        isNewVehicle
        buildyear
        make
        model
        type
        color
        baseColor
        vehicleType
        fuel
        dateOfArrival
      }
    }
  }
  vehicleTradein {
    licensePlate
    odometer
    color
    comments
    fields # json
  }
`;

// these fields are Strapi values, we don't need to send them in the `fields`
const specificFields = [
  'url',
  'vehicleId',
  'leadType',
  'name',
  'street',
  'housenumber',
  'housenumberExt',
  'postalcode',
  'city',
  'country',
  'telephone',
  'email',
  'dateOfBirth',
  'acceptNewsletter',
  'tradein', // deprecated
  'tradeinImages', // deprecated
  'vehicleTradein',
];

const checkFailsDependency = (formValues) => (field) => {
  // if the dependsOn array contains arrays, we have an A or B situation
  if (Array.isArray((field.dependsOn || [])[0])) {
    // if one entry does not fail,
    // some=true, and we return false.
    return !field.dependsOn.some((dep) => !checkFailsDependency(formValues)({ dependsOn: dep }));
  }
  return field.dependsOn.some(
    ({ field: dependency, value: mustContainValue }) => {
      const savedValue = formValues[dependency];
      if (Array.isArray(savedValue)) {
        return !savedValue.includes(mustContainValue);
      }
      return savedValue !== mustContainValue;
    },
  );
};

const removeSpecificFields = (formValues) => Object.keys(formValues)
  .filter((key) => !specificFields.includes(key))
  .reduce((acc, key) => ({
    ...acc,
    [key]: formValues[key],
  }), {});

const cleanData = (formValues, fields, exisitingLead) => Object.entries(formValues)
  .reduce((acc, [key, value]) => {
    const field = fields.find(({ name }) => name === key);
    if (!field) {
      console.error(`[cleanData] There is no field for formValue ${key}. It was filled with value: ${value}. This is pretty strange`);
      return acc;
    }
    // dontSend has been set, we only need this field in the frontend
    if (field.dontSend) {
      return acc;
    }
    // if there is an dependsOn, and that field does not match the value, skip
    if (field.dependsOn?.length > 0) {
      if (checkFailsDependency(formValues)(field)) {
        return acc;
      }
    }

    let theValue = value;
    // e.g. appending lastname to name
    if (field.appendValueOnUpdateLeadTo && exisitingLead?.[field.appendValueOnUpdateLeadTo]) {
      theValue = `${exisitingLead[field.appendValueOnUpdateLeadTo]} ${value}`;
    }

    // if a field is marked as a subfield, rewrite the tree
    if (field.propertyOf) {
      return {
        ...acc,
        [field.propertyOf]: {
          ...acc[field.propertyOf],
          [key]: theValue,
        },
      };
    }

    // all is good.
    return {
      ...acc,
      [key]: theValue,
    };
  }, {});

const addHrefToUrl = (url, href) => {
  const urlAsArray = (url || '').split(',');
  return urlAsArray.concat(!urlAsArray.includes(href) ? href : null).filter(Boolean);
};

const prepareData = (values, href, vdp) => ({
  name: values.name,
  street: values.street,
  housenumber: values.housenumber,
  housenumberExt: values.housenumberExt,
  postalcode: values.postalcode,
  city: values.city,
  country: values.country,
  email: values.email,
  tel: values.telephone,
  dateOfBirth: values.dateOfBirth,
  acceptNewsletter: [].concat(values.acceptNewsletter).includes('true'), // support array for toggle
  // allow url from updateLead to be prepended:
  // dont put the same url twice
  url: String(addHrefToUrl(values.url, href)),
  leadType: String(values.leadType), // array support for multiCheckbox
  // tradein: values.tradein, // deprecated
  // tradeinImages: (values.tradeinImages || []).filter(Boolean), // deprecated
  vehicleTradein: values.vehicleTradein ? {
    ...(values.vehicleTradein || {}),
    images: (values.vehicleTradein?.images || []).filter(Boolean),
  } : undefined,
  fields: removeSpecificFields(values),
  vdp: vdp?.documentId || vdp?.strapiId,
});

const VehicleForm = ({
  fields = defaultFields,
  vehicleId = null, // documentId || strapiId
  updateLead = null,
  navigateToAfterSuccess = null,
  prependContent = null,
  className,
  // for Ligier, when navigate to this page,
  // render <Success /> for Step 2
  renderSuccessOnExistingLead = false,
  ctaText = 'Versturen',
}) => {
  const { vdp } = useVehicle();
  // allow setting values via a Link
  const { state, href } = useLocation();
  const {
    // remove the key that gatsby always adds
    key: _,
    // remove `via` we set in menu links
    via: __,
    // allow transitioning to this page with an exisiting lead
    lead: stateLead,
    ...locationState
  } = state || {};

  // this component allows rendering with a created lead, adjusting its values
  // it can come as a prop, or it can come via a page transition in the history state
  const exisitingLead = updateLead || stateLead;
  const theVehicleId = vehicleId
    || (exisitingLead?.vehicle?.documentId || exisitingLead?.vehicle?.strapiId);

  // get all default values ready, allow a function to set the defaultValue
  const initialFormFields = fields.reduce((result, {
    name,
    value,
    defaultValueFromUpdateLead,
  }) => ({
    ...result,
    [name]: defaultValueFromUpdateLead ? defaultValueFromUpdateLead(exisitingLead || {}) : value,
  }), {});

  // the initial form state is all defaults and what is pushed in the location state
  const [formValues, setValues] = useState({
    ...initialFormFields,
    ...locationState,
  });

  const failsDependency = checkFailsDependency(formValues);

  const [blockFormSendButton, setBlockFormSendButton] = useState(false);

  const onChange = useCallback((name) => (e) => {
    setValues({
      ...formValues,
      [name]: e.target.value,
    });
  }, [
    setValues,
    formValues,
  ]);
  const onChangeToggle = useCallback((name, options) => (e) => {
    const value = ([].concat(formValues[name]) || [])
      .filter((savedValue) => options
        .some(({ value: allowedValue }) => allowedValue === savedValue));

    setValues({
      ...formValues,
      [name]: value.includes(e.target.value)
        ? value.filter((val) => val !== e.target.value)
        : value.concat(e.target.value),
    });
  }, [
    setValues,
    formValues,
  ]);
  const onChangeArray = useCallback((name, index) => (e) => {
    const arr = formValues[name];
    const newArr = [...arr.slice(0, index), e.target.value, ...arr.slice(index + 1)];
    setValues({
      ...formValues,
      [name]: newArr
        .filter(Boolean)
        .concat([undefined]), // add undefined at the end for a new empty input line
    });
  }, [
    setValues,
    formValues,
  ]);

  const inputData = prepareData(
    { ...cleanData(formValues, fields, exisitingLead) },
    href,
    vdp,
  );

  const {
    onSubmit,
    // data,
    lead,
    loading,
    error,
  } = useCreateSubmission(inputData, 'Vehicle lead', {
    vehicleId: theVehicleId,
    ctaText,
    updateLead,
    createAttributes: returnAttributes,
    updateAttributes: returnAttributes,
  });

  if (lead) {
    if (navigateToAfterSuccess) {
      navigate(navigateToAfterSuccess, {
        state: {
          lead,
        },
      });
      return null;
    }

    return <Success lead={lead} />;
  }

  if (renderSuccessOnExistingLead && exisitingLead) {
    return <Success lead={exisitingLead} />;
  }

  return (
    <>
      {prependContent}
      <form
        action=""
        method="POST"
        onSubmit={onSubmit}
        className={className}
      >
        {theVehicleId && <input type="hidden" name="vehicleId" value={theVehicleId} />}
        {fields.map((field) => {
          // if there is an dependsOn, and that field does not match the value, skip
          if (field.dependsOn?.length > 0) {
            if (failsDependency(field)) {
              return null;
            }
          }
          if (field.multi) {
            return formValues[field.name].map((value, index) => (
              <FormField
                key={`${field.key ?? field.name}-${index}`} // eslint-disable-line react/no-array-index-key
                onChange={onChangeArray(field.name, index)}
                {...field}
                setBlockFormSendButton={setBlockFormSendButton}
                value={formValues[field.name][index] || ''}
              />
            ));
          }
          return (
            <FormField
              key={field.key ?? field.name}
              onChange={field.toggle
                ? onChangeToggle(field.name, field.options)
                : onChange(field.name)}
              inLocationState={Boolean(locationState[field.name])}
              {...field}
              setBlockFormSendButton={setBlockFormSendButton}
              value={formValues[field.name] || ''}
            />
          );
        })}
        <Button
          variant="primary"
          type="submit"
          disabled={loading || blockFormSendButton}
        >
          {ctaText}
        </Button>
        {/* @TODO: checken hoe op plugins-forms we met error omgaan, daar heb ik het mooi */}
        {error && (
          <Alert $danger>
            Er is helaas iets mis gegaan, probeer het later nog eens.
          </Alert>
        )}
      </form>
    </>
  );
};

export default styled(VehicleForm)``;
